<script setup>
import { ref, reactive, computed, onMounted, watch } from "vue";
import {
	EmptyPage,
	PageHeading,
	Input,
	Select,
	Button,
	Icon,
} from "@components";

import CategoryList from "./CategoryList.vue";
import CategoryListDraggable from "./CategoryListDraggable.vue";

import { useService } from "@store/service.js";
const $service = useService();

import { useSettings } from "@store/settings";
const $settings = useSettings();

onMounted(() => {
	$service.fetchData();
});

const emptyPageContent = {
	heading: {
		title: `You haven't created any ${String(
			$settings.labels.service?.singular
		).toLowerCase()}`,
		subtitle: `To create a new ${String(
			$settings.labels.service?.singular
		).toLowerCase()}, simply click on the 'Add New' button. Alternatively, you can create a new ${String(
			$settings.labels.service?.singular
		).toLowerCase()} by clicking on '${String(
			$settings.labels.service?.singular
		).toLowerCase()}'.`,
	},
	buttonCta: [
		{
			title: `${$settings.labels.service?.singular}`,
			subtitle: `${$settings.labels.service?.plural} which can be booked individually`,
			icon: "star04",
			link: { name: "service.add", query: { tab: "general" } },
		},
	],
};

const filter = ref({
	search: "",
	category: "all",
});

const getCategoryOptions = computed(() => {
	let options = [{ label: "All Categories", value: "all" }];

	$service.getCategories.map((category) => {
		options.push({
			label: category.name,
			value: category.category_id,
		});
	});

	return options;
});

// const newFilteredCategory = ref([])

const filteredCategory = computed(() => {
	let category = [];

	$service.getCategories.forEach((cat) => {
		category.push({ ...cat });
	});

	// filter categories by category
	if (filter.value.category && filter.value.category !== "all") {
		category = category.filter(
			(cat) => cat.category_id == filter.value.category
		);
	}

	// filter service by search
	if (filter.value.search) {
		// filter category by search
		category = category.filter((cat) => {
			// match with category name
			let catNameMatch = String(cat.name.toLowerCase()).includes(
				filter.value.search.toLowerCase()
			);
			// console.log(catNameMatch)
			// match with service name
			if (!catNameMatch) {
				cat.services = cat.services.filter((ser) =>
					String(ser.name)
						.toLowerCase()
						.includes(filter.value.search.toLowerCase())
				);
			}
			return cat;
		});

		// remove empty services category after search if not match with category name
		category = category.filter(
			(cat) =>
				String(cat.name)
					.toLowerCase()
					.includes(filter.value.search.toLowerCase()) ||
				cat.services.length > 0
		);
	}

	// remove Uncategorised if it's service empty
	if (
		category.length == 1 &&
		category[0].name == "Uncategorised" &&
		!category[0].services.length
	) {
		category.splice(0, 1);
	}

	return category;
});

const subtitle = computed(() => {
	let category = filteredCategory.value;
	let services = [];

	filteredCategory.value.forEach((cat) => {
		cat.services.forEach((s) => {
			services.push(s);
		});
	});
	return (
		category.length +
		" categor" +
		(category.length > 1 ? "ies" : "y") +
		" and " +
		services.length +
		` ${String($settings.labels.service?.singular).toLowerCase()}` +
		(services.length > 1 ? "s" : "")
	);
});

const showList = computed(() => {
	// console.log($service.getCategories.length == 1 && !$service.getServices.length && $service.getCategories.find(cat => cat.name == 'Uncategorised') ? true : false)
	if (!$service.getCategories.length) return false;
	else if (
		$service.getCategories.length == 1 &&
		!$service.getServices.length &&
		$service.getCategories.find((cat) => cat.name == "Uncategorised")
			? true
			: false
	)
		return false;
	else return true;
});
</script>


<template>
	<div class="md:max-w-[992px] m-auto relative">
		<div v-if="!$service.isLoaded" class="absolute top-0 left-0 z-10 flex flex-col w-full gap-6 bg-white fade-out max-h-svh">
			<div class="w-full bg-gray-200 rounded-md animate-pulse max-w-56 h-14"></div>
			<div v-for="n in 2" :key="n" class="flex flex-col w-full gap-5 overflow-hidden">
				<div class="w-full bg-gray-200 rounded-md animate-pulse h-14"></div>
				<div v-for="n in 2" :key="n" class="w-full h-16 ml-8 bg-gray-200 rounded-md animate-pulse"></div>
			</div>
		</div>

		<div v-else>
			<!-- Empty page -->
			<EmptyPage v-if="!showList" :pageContent="emptyPageContent" />
			<!-- List items  -->
			<div v-else class="fade-in">
				<PageHeading :title="$settings.labels.service?.plural" :subtitle="subtitle">
					<template #rightSide>
						<router-link v-if="access('service.add')" :to="{ name: 'service.add', query: { tab: 'general' } }">
							<Button size="md" type="tertiary_color" radius="square">
								Add New
								<template #before>
									<Icon name="plusCircle" />
								</template>
							</Button>
						</router-link>
					</template>
				</PageHeading>

				<div class="flex gap-[52px] my-7 justify-between items-center w-full">
					<div class="w-full">
						<Input v-model="filter.search" class="w-full" :placeholder="`Search ${String($settings.labels.service?.plural).toLowerCase()}`" iconLeft="searchLg"></Input>
					</div>
					<div>
						<Select :options="getCategoryOptions" v-model="filter.category" placeholder="Category" class="w-[168px]"></Select>
					</div>
				</div>

				<CategoryListDraggable v-if="access('service.add')" :categories="filteredCategory"></CategoryListDraggable>
				<CategoryList v-else :categories="filteredCategory"></CategoryList>
			</div>
		</div>
	</div>
</template>